import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAccount } from '@/hooks/use-accounts'
import { usePools } from '@/hooks/use-pools'
import { useIncognito } from '@/hooks/use-incognito'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, ArrowRight, Check, Copy, Info, Loader2 } from 'lucide-react'
import { AccountIcon, PoolIcon } from '@/lib/entity-icons'
import { formatDate } from '@/lib/utils'

export default function AccountDetailPage() {
  const { accountID } = useParams<{ accountID: string }>()
  const navigate = useNavigate()
  const { data: account, isLoading } = useAccount(accountID ?? '')
  const { data: pools = [] } = usePools()
  const incognito = useIncognito()
  const [copied, setCopied] = useState(false)

  const accountPools = pools.filter((p) => p.accountID === accountID)

  const handleCopyId = async () => {
    if (!accountID) return
    try {
      await navigator.clipboard.writeText(accountID)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!account) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/accounts')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Accounts
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Account not found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const displayName = incognito.isIncognito
    ? incognito.accountName(accountID ?? '')
    : account.name

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/accounts')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          {!incognito.isIncognito && account.customization?.logo ? (
            <img
              src={account.customization.logo}
              alt=""
              className="h-8 w-auto max-w-[72px] object-contain"
            />
          ) : (
            <AccountIcon className="h-8 w-8 text-muted-foreground" />
          )}
          <h1 className="text-3xl font-bold tracking-tight">{displayName}</h1>
          {account.kind && <Badge variant="secondary">{account.kind}</Badge>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <Info className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pools">
            <PoolIcon className="mr-2 h-4 w-4" />
            Pools ({accountPools.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Account ID</span>
                  <div className="flex items-center gap-2 font-mono text-xs">
                    {incognito.isIncognito ? (
                      <span>{incognito.uuid(accountID ?? '')}</span>
                    ) : (
                      <>
                        <span className="truncate max-w-[180px]">{accountID}</span>
                        <button
                          onClick={handleCopyId}
                          className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                          title="Copy Account ID"
                        >
                          {copied ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {account.kind && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Kind</span>
                    <Badge variant="outline">{account.kind}</Badge>
                  </div>
                )}
                {account.solution && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Solution</span>
                    <span className="text-sm">{account.solution}</span>
                  </div>
                )}
                {account.systemsCount !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Systems</span>
                    <span className="text-sm font-medium">{account.systemsCount}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {account.createdAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Created</span>
                    <span className="text-sm">{formatDate(account.createdAt)}</span>
                  </div>
                )}
                {account.updatedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Last updated</span>
                    <span className="text-sm">{formatDate(account.updatedAt)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pools" className="mt-4">
          {accountPools.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <PoolIcon className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No pools for this account</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {accountPools.map((pool) => (
                <Link
                  key={pool.poolID}
                  to={`/pools/${pool.poolID}`}
                  className="flex items-center justify-between gap-4 rounded-lg border p-3 hover:bg-muted transition-colors group"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <PoolIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span className="font-medium truncate">{incognito.poolName(pool.name)}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-sm text-muted-foreground">
                    <span>
                      {pool.systemCount} system{pool.systemCount !== 1 ? 's' : ''}
                    </span>
                    <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
